import React from "react";
import { Clock, TrendingDown, Trophy, AlertTriangle, Check, X, Train } from "lucide-react";
import { Badge } from "./ui/badge";

const MythBusterSection = () => {
  const investors = [
    {
      id: "waiter",
      name: "The Waiter",
      icon: Clock,
      description: "Waited for the 'perfect dip'",
      behavior: "Always waiting for market to fall before investing",
      invested: "₹12,00,000",
      corpus: "₹18,50,000",
      returns: "54%",
      status: "average",
      color: "amber",
      traits: [
        { text: "Missed multiple rallies", positive: false },
        { text: "Analysis paralysis", positive: false },
        { text: "Invested only 60% of planned", positive: false },
      ],
    },
    {
      id: "panicker",
      name: "The Panicker",
      icon: TrendingDown,
      description: "Stopped SIP during market falls",
      behavior: "Panicked and stopped investing during corrections",
      invested: "₹8,00,000",
      corpus: "₹9,20,000",
      returns: "15%",
      status: "poor",
      color: "red",
      traits: [
        { text: "Sold at market lows", positive: false },
        { text: "Stopped SIPs during COVID crash", positive: false },
        { text: "Missed recovery gains", positive: false },
      ],
    },
    {
      id: "disciplined",
      name: "The SIP Investor",
      icon: Trophy,
      description: "Invested consistently via SIP",
      behavior: "Never missed a SIP, regardless of market conditions",
      invested: "₹18,00,000",
      corpus: "₹42,00,000",
      returns: "133%",
      status: "winner",
      color: "emerald",
      traits: [
        { text: "Rupee cost averaging benefit", positive: true },
        { text: "Stayed invested through volatility", positive: true },
        { text: "Automated wealth creation", positive: true },
      ],
    },
  ];

  return (
    <section
      id="myth-buster"
      className="py-24 sm:py-32 bg-gradient-to-b from-white to-emerald-50/30"
      data-testid="myth-buster-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">
            <AlertTriangle className="w-4 h-4" />
            The Market Timing Myth
          </span>
          <h2
            className="text-3xl sm:text-4xl tracking-tight font-semibold leading-snug text-emerald-950 mb-4"
            data-testid="myth-buster-title"
          >
            Why Waiting for the 'Right Time' is Costing You Crores
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We analyzed 15 years of data. The results will surprise you.{" "}
            <strong className="text-emerald-700">
              Time in the market beats timing the market.
            </strong>
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {investors.map((investor) => (
            <div
              key={investor.id}
              className={`relative rounded-3xl p-6 md:p-8 transition-all duration-300 hover-lift ${
                investor.status === "winner"
                  ? "comparison-winner shadow-xl shadow-emerald-500/20"
                  : "bg-white border border-slate-200 shadow-lg"
              }`}
              data-testid={`investor-card-${investor.id}`}
            >
              {/* Winner Badge */}
              {investor.status === "winner" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-emerald-500 text-white px-4 py-1 winner-badge">
                    <Trophy className="w-3 h-3 mr-1" />
                    WINNER
                  </Badge>
                </div>
              )}

              {/* Icon & Title */}
              <div className="text-center mb-6 pt-2">
                <div
                  className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                    investor.color === "emerald"
                      ? "bg-emerald-100 text-emerald-600"
                      : investor.color === "amber"
                      ? "bg-amber-100 text-amber-600"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  <investor.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-emerald-950">
                  {investor.name}
                </h3>
                <p className="text-sm text-slate-500 mt-1">{investor.description}</p>
              </div>

              {/* Stats */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Total Invested</span>
                  <span className="font-semibold text-slate-900">
                    {investor.invested}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Final Corpus</span>
                  <span
                    className={`font-bold text-lg ${
                      investor.color === "emerald"
                        ? "text-emerald-600"
                        : investor.color === "amber"
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    {investor.corpus}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-slate-600">Total Returns</span>
                  <Badge
                    variant={investor.status === "winner" ? "default" : "secondary"}
                    className={
                      investor.status === "winner"
                        ? "bg-emerald-500 text-white"
                        : investor.status === "poor"
                        ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-700"
                    }
                  >
                    {investor.returns}
                  </Badge>
                </div>
              </div>

              {/* Traits */}
              <div className="space-y-2">
                {investor.traits.map((trait, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 text-sm ${
                      trait.positive ? "text-emerald-700" : "text-slate-500"
                    }`}
                  >
                    {trait.positive ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400" />
                    )}
                    {trait.text}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Train Analogy */}
        <div
          className="bg-emerald-900 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden"
          data-testid="train-analogy"
        >
          {/* Train Animation Background */}
          <div className="absolute inset-0 opacity-10">
            <div className="train-animate absolute top-1/2 -translate-y-1/2">
              <Train className="w-24 h-24" />
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                The Mumbai Local Analogy
              </h3>
              <p className="text-emerald-100 text-lg leading-relaxed">
                Imagine waiting at Dadar station for an "empty train." Meanwhile, others
                keep boarding whatever train comes. After 10 years, who reaches their
                destination?{" "}
                <strong className="text-white">
                  The one who kept boarding, not the one still waiting.
                </strong>
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <p className="text-sm text-emerald-200 uppercase tracking-wider mb-2">
                Key Insight
              </p>
              <p className="text-2xl md:text-3xl font-bold">
                ₹24 Lakh difference in 15 years
              </p>
              <p className="text-emerald-200 mt-2">
                Between The Waiter and The SIP Investor (same monthly capacity)
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MythBusterSection;
